import encryptionService from './encryptionService'
import type { JournalEntry } from '@/pages/journal/config/journal.types'

/**
 * Secure storage service that encrypts journal data before storing in IndexedDB
 * Uses the existing encryptionService for cryptographic operations
 */
export class SecureStorageService {
  private encryptionKey: CryptoKey | null = null
  private readonly STORAGE_SALT = 'TalkingParents-Journal-v1' // Static salt for key derivation

  /**
   * Initialize encryption key based on user session/data
   * This should be called when user logs in or when journal is first accessed
   */
  async initializeEncryption(userIdentifier: string): Promise<void> {
    try {
      // Use user identifier (e.g., user ID, session token) to derive encryption key
      this.encryptionKey = await encryptionService.generateKeyFromData(
        userIdentifier,
        this.STORAGE_SALT
      )
    } catch (error) {
      console.error('[SecureStorageService] Failed to initialize encryption:', error)
      throw error
    }
  }

  /**
   * Encrypt journal entries for secure storage
   */
  async encryptJournalEntries(entries: JournalEntry[]): Promise<string> {
    if (!this.encryptionKey) {
      throw new Error('Encryption key not initialized. Call initializeEncryption() first.')
    }

    const jsonData = JSON.stringify(entries)
    const { iv, encryptedData } = await encryptionService.encryptData(this.encryptionKey, jsonData)

    // Combine IV and encrypted data for storage
    const combinedData = {
      iv: Array.from(new Uint8Array(iv)),
      data: Array.from(new Uint8Array(encryptedData))
    }

    return JSON.stringify(combinedData)
  }

  /**
   * Decrypt journal entries from secure storage
   */
  async decryptJournalEntries(encryptedData: string): Promise<JournalEntry[]> {
    if (!this.encryptionKey) {
      throw new Error('Encryption key not initialized. Call initializeEncryption() first.')
    }

    try {
      const combinedData = JSON.parse(encryptedData)
      const iv = new Uint8Array(combinedData.iv)
      const data = new Uint8Array(combinedData.data)

      const decryptedJson = await encryptionService.decryptData(this.encryptionKey, iv, data)

      return JSON.parse(decryptedJson)
    } catch (error) {
      throw error
    }
  }

  /**
   * Encrypt any JSON serializable data
   */
  async encryptData<T>(data: T): Promise<string> {
    if (!this.encryptionKey) {
      throw new Error('Encryption key not initialized. Call initializeEncryption() first.')
    }

    const jsonData = JSON.stringify(data)
    const { iv, encryptedData } = await encryptionService.encryptData(this.encryptionKey, jsonData)

    const combinedData = {
      iv: Array.from(new Uint8Array(iv)),
      data: Array.from(new Uint8Array(encryptedData))
    }

    return JSON.stringify(combinedData)
  }

  /**
   * Decrypt any JSON serializable data
   */
  async decryptData<T>(encryptedData: string): Promise<T> {
    if (!this.encryptionKey) {
      throw new Error('Encryption key not initialized. Call initializeEncryption() first.')
    }

    const combinedData = JSON.parse(encryptedData)
    const iv = new Uint8Array(combinedData.iv)
    const data = new Uint8Array(combinedData.data)

    const decryptedJson = await encryptionService.decryptData(this.encryptionKey, iv, data)

    return JSON.parse(decryptedJson)
  }

  /**
   * Clear encryption key from memory (e.g., on logout)
   */
  clearEncryptionKey(): void {
    this.encryptionKey = null
  }

  /**
   * Check if encryption is initialized
   */
  isEncryptionReady(): boolean {
    return this.encryptionKey !== null
  }
}

// Export singleton instance
export default new SecureStorageService()
